import { Plus } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Mascot } from './Mascot';
import { AGENT_TONES } from './tones';

export interface NewAgentButtonProps {
  label: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

/** How long each tone shows while the button is hovered. */
const TONE_STEP_MS = 320;
/**
 * The Blueticks AI blues — the mascot's head and the "1 minute" title (blue → royal
 * → indigo → violet, back to blue so the loop is seamless), drifting on their own.
 * At rest they are only the pill's border (a white pill, so the blue brand face
 * reads); on hover they fill it, and white text reads on all of them.
 */
const PILL_GRADIENT = 'linear-gradient(120deg, #2563eb 0%, #1d4ed8 20%, #4338ca 40%, #6366f1 60%, #3b82f6 80%, #2563eb 100%)';
/** One drift across the gradient; hover plays it faster. */
const DRIFT_MS = 9000;
const HOVER_RATE = 4;
const BADGE_BLUE = '#2563eb';
/** The hand at rest: a light brand blue, so its finger strokes stay readable. */
const BRAND_HAND = '#93c5fd';
const BADGE_SKY = '#bfe3ff';
/** At rest the "+" turns into a waving hand every WAVE_EVERY_MS, for WAVE_SHOW_MS. */
const WAVE_EVERY_MS = 6000;
const WAVE_FIRST_MS = 2500;
const WAVE_SHOW_MS = 1900;
/** The hello wave (from the wrist) and the V sign's gentle rock. */
const HELLO: Keyframe[] = [{ transform: 'rotate(-16deg)' }, { transform: 'rotate(18deg)' }];
const ROCK: Keyframe[] = [{ transform: 'rotate(-6deg)' }, { transform: 'rotate(8deg)' }];

type Gesture = 'plus' | 'wave' | 'v';

const HAND_STROKE = { stroke: 'rgba(10,11,13,0.5)', strokeWidth: 1.2, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };

/** 👋 — an open hand tipped to the side: four fingers together, the thumb out, two motion ticks. */
const WavingHand: React.FC<{ tone: string }> = ({ tone }) => (
  <svg viewBox="0 0 24 24" className="block h-full w-full overflow-visible" aria-hidden>
    <g transform="rotate(-18 13 16)" fill={tone} {...HAND_STROKE}>
      <rect x={6.4} y={4.2} width={3.3} height={10} rx={1.65} />
      <rect x={9.6} y={2.6} width={3.3} height={11.5} rx={1.65} />
      <rect x={12.8} y={3.2} width={3.3} height={11} rx={1.65} />
      <rect x={16} y={5.4} width={3.1} height={9} rx={1.55} />
      <rect x={5.1} y={10.2} width={3.4} height={8.6} rx={1.7} transform="rotate(-38 6.8 18.8)" />
      <path d="M6.3 11.2h12.9v4.6c0 3.9-2.9 6.7-6.6 6.7s-6.3-2.8-6.3-6.4z" />
    </g>
    <g fill="none" stroke="currentColor" strokeWidth={1.3} strokeLinecap="round" opacity={0.75}>
      <path d="M2.2 6.2c-.9 1.2-1.1 2.6-.7 3.9" />
      <path d="M4.6 3.2c-1 .9-1.5 2-1.5 3.2" />
    </g>
  </svg>
);

/** ✌️ — two fingers up in a V, the other two folded, the thumb tucked across them. */
const VSignHand: React.FC<{ tone: string }> = ({ tone }) => (
  <svg viewBox="0 0 24 24" className="block h-full w-full overflow-visible" aria-hidden>
    <g fill={tone} {...HAND_STROKE}>
      <rect x={6.3} y={1.6} width={3.5} height={12} rx={1.75} transform="rotate(-13 8.05 13.6)" />
      <rect x={11.6} y={1.2} width={3.5} height={12.4} rx={1.75} transform="rotate(11 13.35 13.6)" />
      <path d="M5.8 13c0-1.5 1-2.4 2.5-2.4h7.9c1.9 0 3.1 1.1 3.1 3v3.1c0 3.4-2.6 5.8-6 5.8h-1.6c-3.3 0-5.9-2.5-5.9-5.8z" />
      <path d="M15.4 13.4h3.4M15.2 16.1h3.3" fill="none" />
      <path d="M7 17.2c2-1.3 4.6-1.5 7.4-.4" fill="none" />
    </g>
  </svg>
);

/**
 * THE "New agent" call to action (the /agents page, Quick Agents): a white pill
 * with a border of slowly drifting Blueticks blues, the brand face and ONE gesture at its side:
 * a "+" at rest, which every few seconds turns into a hand waving hello, and on
 * hover into a V sign (each swap a crossfade with a little pop and turn).
 *
 * Hover fills the pill with the blues, makes the face wide-eyed and runs it through the agent tones, the drift speeds up, the pill lifts on a glow of the
 * current tone, and a sheen sweeps across. At rest the face just blinks. Reduced motion keeps it
 * still (no tone cycling; the Mascot itself goes static).
 */
export const NewAgentButton: React.FC<NewAgentButtonProps> = ({ label, onClick, disabled, className = '', 'data-testid': testId }) => {
  const [hover, setHover] = useState(false);
  const [step, setStep] = useState(0);
  const ref = useRef<HTMLButtonElement>(null);
  const drift = useRef<Animation | null>(null);
  const waveHand = useRef<HTMLSpanElement>(null);
  const vHand = useRef<HTMLSpanElement>(null);
  const [waving, setWaving] = useState(false);
  const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // The background drift (Web Animations: no global keyframes needed).
  useEffect(() => {
    const el = ref.current;
    if (!el?.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    drift.current = el.animate([{ backgroundPosition: '0% 50%' }, { backgroundPosition: '100% 50%' }], {
      duration: DRIFT_MS,
      iterations: Infinity,
      direction: 'alternate',
      easing: 'ease-in-out',
    });
    return () => drift.current?.cancel();
  }, []);

  useEffect(() => {
    if (drift.current) drift.current.playbackRate = hover ? HOVER_RATE : 1;
  }, [hover]);

  // At rest: now and then the "+" becomes a hand waving hello, then goes back.
  useEffect(() => {
    if (hover || reducedMotion()) return undefined;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const wave = () => {
      setWaving(true);
      timers.push(setTimeout(() => setWaving(false), WAVE_SHOW_MS));
    };
    timers.push(setTimeout(wave, WAVE_FIRST_MS));
    const every = setInterval(wave, WAVE_EVERY_MS);
    return () => {
      clearInterval(every);
      timers.forEach(clearTimeout);
      setWaving(false);
    };
  }, [hover]);

  const gesture: Gesture = hover ? 'v' : waving ? 'wave' : 'plus';

  // The hands move only while showing: the hello wave, and the V sign's rock.
  useEffect(() => {
    const el = gesture === 'wave' ? waveHand.current : gesture === 'v' ? vHand.current : null;
    if (!el?.animate || reducedMotion()) return undefined;
    const anim = gesture === 'wave'
      ? el.animate(HELLO, { duration: 280, delay: 180, iterations: 5, direction: 'alternate', easing: 'ease-in-out' })
      : el.animate(ROCK, { duration: 900, delay: 300, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' });
    return () => anim.cancel();
  }, [gesture]);

  useEffect(() => {
    if (!hover || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = setInterval(() => setStep((s) => s + 1), TONE_STEP_MS);
    return () => clearInterval(timer);
  }, [hover]);

  // At rest the brand face (blue, on the white pill); hovered, the light agent tones on the blue fill.
  const tone = hover ? AGENT_TONES[step % AGENT_TONES.length] : undefined;
  const badge = hover ? BADGE_SKY : BADGE_BLUE;
  // The hand is the face's colour: the brand blue at rest, the cycling tone on hover.
  const handTone = tone ?? BRAND_HAND;

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className={`group relative isolate inline-flex h-12 items-center gap-3 overflow-hidden rounded-full ps-1.5 pe-6 text-[15px] font-semibold tracking-[-0.01em] outline-none transition-[transform,box-shadow] duration-300 ease-ai hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-ai-focus-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${className}`}
      style={{
        color: hover ? '#fff' : 'var(--ai-text)',
        border: '2px solid transparent',
        // Rest: a white fill inside the padding box over the gradient = a gradient border only.
        backgroundImage: hover ? PILL_GRADIENT : `linear-gradient(var(--ai-surface), var(--ai-surface)), ${PILL_GRADIENT}`,
        backgroundClip: hover ? 'border-box' : 'padding-box, border-box',
        backgroundOrigin: 'border-box',
        backgroundSize: '300% 100%',
        boxShadow: hover
          ? `0 14px 30px -12px ${tone}, inset 0 0 0 1px rgba(255,255,255,0.18)`
          : '0 8px 22px -14px rgba(37,99,235,0.6)',
      }}
      data-testid={testId}
    >
      {/* The sheen: one light sweep across the pill on hover. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -start-1/2 -z-10 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 transition-[inset-inline-start,opacity] duration-700 ease-out group-hover:start-[120%] group-hover:opacity-100"
      />
      <span className="relative grid h-10 w-10 shrink-0 place-items-center">
        {/* Hovered: excited — wide, bright eyes looking up at you. */}
        <Mascot size={34} tone={tone} mood={hover ? 'excited' : 'idle'} level="ambient" />
        {/* The gesture slot, tucked against the face's lower corner (inside the pill's padding). */}
        <span aria-hidden className="absolute bottom-0.5 -end-2 h-[18px] w-[18px]">
          <GestureLayer on={gesture === 'plus'}>
            <Plus className="h-3.5 w-3.5" strokeWidth={3.5} style={{ color: badge, filter: `drop-shadow(0 0 3px ${badge})` }} />
          </GestureLayer>
          <GestureLayer on={gesture === 'wave'} handRef={waveHand}>
            <WavingHand tone={handTone} />
          </GestureLayer>
          <GestureLayer on={gesture === 'v'} handRef={vHand}>
            <VSignHand tone={handTone} />
          </GestureLayer>
        </span>
      </span>
      <span className="relative truncate">{label}</span>
    </button>
  );
};

/**
 * One gesture in the slot. Swaps crossfade with a small pop and turn from the
 * wrist; `handRef` is the inner node the hand's own motion (wave/rock) runs on,
 * so it never fights the swap transition.
 */
const GestureLayer: React.FC<{ on: boolean; handRef?: React.Ref<HTMLSpanElement>; children: React.ReactNode }> = ({ on, handRef, children }) => (
  <span
    className={`absolute inset-0 grid place-items-center origin-bottom transition-[opacity,scale,rotate] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${on ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-50 -rotate-30'}`}
  >
    <span ref={handRef} className="grid h-full w-full origin-bottom place-items-center">{children}</span>
  </span>
);
